import { Razor } from "../razor/razor";

export class ChipsideComponent extends HTMLElement {
	#ObservableBooleans = ["disabled"];

	constructor() {
		super();

		this.init = new WeakMap;
		this.queue = [];
		this._onparsed = null;
		this.parsed = false;
		this.setQueue = [];

		this.disabled = this.hasAttribute("disabled");
		this.badge = this.getAttribute("badge");
		this.tooltip = this.getAttribute("tooltip");

		this.normalizeContent = true;
		this.enhanceContents = this.hasAttribute("enhance");

		const observable = this.constructor.observedAttributes ?? [];
		observable.push("disabled", "tooltip");

		for (const attribute of observable) {
			let value = this.getAttribute(attribute),
				propertyName = attribute.replace(/-([a-z]|\d)/ig, (_, letter) => letter.toUpperCase());

			// The regex converts something like 'button-style' to 'buttonStyle'
			Object.defineProperty(this, propertyName, {
				get() {
					return this.#ObservableBooleans.includes(attribute) ? this.hasAttribute(attribute) : value;
				},
				set(val) {
					if ([true, false].includes(val) && !this.#ObservableBooleans.includes(attribute)) {
						this.#ObservableBooleans.push(attribute);
					}

					const setValue = () => {
						if (val === false && this.hasAttribute(attribute)) {
							this.removeAttribute(attribute);
						} else if (val === true && !this.hasAttribute(attribute)) {
							this.setAttribute(attribute, "");
						} else if (!this.#attributeEquals(attribute, val) && ![true, false].includes(val)) {
							if (val?.toString().trim() === "") { this.removeAttribute(attribute); }
							else { this.setAttribute(attribute, val); }
						}

						if (attribute === "tooltip") {
							const existing = document.querySelector(`#${this.__tooltipID} .tooltip-content`);

							if (existing) {
								if (val) {
									existing.textContent = val;
								} else {
									existing.remove();
								}
							} else {
								Razor.RefreshTooltips();
							}
						}
					}

					value = val;

					if (!this.parsed) {
						this.setQueue.push(setValue);
					} else {
						setValue();
					}
				}
			});
		}
	}

	get onparsed() {
		return this._onparsed;
	}

	set onparsed(value) {
		if (this.parsed) { value?.(); }
		else { this._onparsed = value; }
	}

	attributeChangedCallback(name, oldValue, newValue) {
		if (!this.parsed) { return; }

		this.attributeChanged?.(name, oldValue, newValue);
	}

	/**
	 * For a boolean attribute, this is a helper function to check whether the attribute should exist or not
	 * @param {*} value The value to check
	 * @return {boolean} True if the attribute is to exist
	 */
	IsAttributeValueTrue(value) {
		return [true, ""].includes(value);
	}

	#attributeEquals(attributeName, value) {
		const attrValue = this.getAttribute(attributeName);

		if (attrValue?.toString() === value?.toString()) { return true; }

		return attrValue == null && ["null", "undefined"].includes(value?.toString());
	}

	raiseEvent(eventName) {
		const handler = this[`on${eventName.toLowerCase()}`];

		if (handler) {
			handler();
		} else {
			this.dispatchEvent(new Event(eventName, {
				view: window,
				bubbles: false,
				cancelable: false
			}));
		}
	}

	click() {
		this.raiseEvent("click");
	}

	disconnectedCallback() {
		document.getElementById(this.__tooltipID)?.remove();
	}

	connectedCallback() {
		const parsedCallback = el => {
			this.queue = this.queue.filter(([_, value]) => value !== "parsedCallback");

			if (!this.queue.length) {
				requestAnimationFrame(() => {
					this.queue.splice(0).forEach(([element, fnName]) => {
						if (this.init.get(element) !== true && !this.parsed) {
							this.init.set(element, true);

							if (this.normalizeContent && this.firstChild?.nodeType === Node.TEXT_NODE) { this.firstChild.trimBreaks(); }
							if (this.enhanceContents) { this.enhance(); }
							element[fnName]?.();
							this.parsed = true;

							this.setQueue.forEach(fn => fn());

							if (this.tooltip || this.querySelector("[tooltip]")) {
								Razor.RefreshTooltips();
							}

							setTimeout(() => {
								this.raiseEvent("parsed");
							}, 0);
						}
					});
				});
			}

			this.queue.push([el, "parsedCallback"]);
		}

		const isParsed = el => {
			do {
				if (el.nextSibling) {
					return true;
				}
			} while (el = el.parentNode);

			return false;
		}

		const cleanUp = (el, observer, ownerDocument, onDCL) => {
			observer.disconnect();
			ownerDocument.removeEventListener("DOMContentLoaded", onDCL);

			parsedCallback(el);
		}

		const self = this;
		const { ownerDocument } = self;

		this.init.set(self, false);

		if (ownerDocument.readyState === 'complete' || isParsed(self)) {
			parsedCallback(self);
		}
		else {
			const onDCL = () => cleanUp(self, observer, ownerDocument, onDCL);
			ownerDocument.addEventListener("DOMContentLoaded", onDCL);

			const observer = new MutationObserver(() => {
				if (isParsed(self)) {
					cleanUp(self, observer, ownerDocument, onDCL);
				}
			});

			observer.observe(self.parentNode, { childList: true, subtree: true });
		}
	}
}